import { Box, Typography } from '@mui/material';
import { Business_Profile_Enum, Measurement_Unit_Enum } from '@kheops/graphql';
import { formatSuggestedRetailPrice } from '@kheops/utils';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router';
import FileUtils from '../common/utils/file.utils';
import SearchUtils from '../common/utils/search.utils';
import { ellipsisStyle, linkStyle } from '../common/utils/style.utils';
import useCurrencyFormat from '../hooks/useCurrencyFormat';
import { ProductCardData } from '../products/common/product-card.types';
import QualityLabels from '../search/product/QualityLabels';
import { RoutePaths } from '../routes/AppRoutes';
import { packagingSheetSelectedProductAtom } from '../state';
import SocialTags from '../social-proof/SocialTags';
import WishlistContactButton from './WishlistContactButton';
import WishlistProductButton from './WishlistProductButton';

const IMAGE_WIDTH = 198;

interface WishlistProductRowProps {
  product: ProductCardData;
}

export default function WishlistProductRow({ product }: WishlistProductRowProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'price', 'products']);
  const currencyFormat = useCurrencyFormat();
  const setSelectedProduct = useSetAtom(packagingSheetSelectedProductAtom);

  const handleProductClick = (): void => {
    setSelectedProduct(product);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        gap: 3,
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          flexShrink: 0,
          bgcolor: 'white',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <Box
          onClick={handleProductClick}
          sx={{ display: 'block', width: IMAGE_WIDTH, cursor: 'pointer' }}
        >
          <Box
            component="img"
            src={FileUtils.BuildPackagingImageUrl({ photo: product.photo, type: product.family, size: { height: 396, fit: 'contain' } })}
            alt={t('common:product_one')}
            sx={{
              width: IMAGE_WIDTH,
              aspectRatio: '1 / 1',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>
        {!!product.labels.length && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              zIndex: 5,
            }}
          >
            <QualityLabels
              labels={product.labels}
              nbToDisplay={2}
              size={32}
              iconSx={{
                height: 32,
                width: 32,
                textAlign: 'center',
              }}
            />
          </Box>
        )}
        <Box sx={{ position: 'absolute', bottom: 12, right: 12, zIndex: 5 }}>
          <WishlistProductButton
            supplierId={product.companyId}
            productSku={product.productSku}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          pt: 1,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box onClick={handleProductClick} sx={{ ...linkStyle, cursor: 'pointer' }}>
            <Typography variant="titleMedium" sx={ellipsisStyle}>
              {product.name}
            </Typography>
          </Box>
          <Typography variant="bodySmall" color="onSurfaceVariant">
            {product.baseUnitLabel}
            {product.packagingLabel && ` - ${product.packagingLabel}`}
          </Typography>
        </Box>
        {!!product.socialTags.length && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <SocialTags tags={product.socialTags} displayRules={{ rules: { 1: 1, 2: 2 } }} />
          </Box>
        )}
        <Box>
          <Typography variant="titleMedium">
            {SearchUtils.getFormatedUnitPrice(product, currencyFormat.format)}
          </Typography>
          {product.suggestedRetailPrice && (
            <Typography variant="bodySmall" color="onSurfaceVariant">
              {t('price:suggest_retail_price_acronym')} : {
                formatSuggestedRetailPrice({
                  suggestedRetailPrice: product.suggestedRetailPrice,
                  billingType: product.billingType,
                  unitType: product.baseUnitType,
                  netContent: (product.baseUnitNetContent || product.packagingNetContent) as number,
                  measurementUnit: (product.baseUnitMeasurementUnit || product.packagingMeasurementUnit) as Measurement_Unit_Enum,
                })
              }
            </Typography>
          )}
        </Box>
        {product.company && (
          <Box
            component={Link}
            to={generatePath(RoutePaths.CONTRACT_COMPANY, { companyId: product.companyId })}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              maxWidth: '100%',
              color: 'onSurface.main',
            }}
          >
            <Box
              component="img"
              src={FileUtils.BuildCompanyImageUrl({ photo: product.companyPhoto, type: Business_Profile_Enum.Supplier, size: { height: 32, fit: 'contain' } })}
              sx={{
                height: 32,
                width: 32,
                borderRadius: '50%',
                flexShrink: 0,
              }}
              alt={t('common:company')}
            />
            <Typography variant="bodySmall" sx={ellipsisStyle}>
              {product.company}
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ pt: 1, flexShrink: 0 }}>
        <WishlistContactButton supplierId={product.companyId} />
      </Box>
    </Box>
  );
}
